import {
  FlowchartLabelOrigin,
  FlowchartNodeShape,
  type FlowchartEdge,
  type FlowchartNode,
  type ParsedFlowchart,
} from './parse-mermaid-flowchart';

export type ResolvedFlowchart = ParsedFlowchart;

function clusterAnchor(subgraphId: string): string {
  return subgraphId;
}

function unique(values: readonly string[]): string[] {
  return values.reduce<string[]>((result, value) => {
    return result.includes(value) ? result : [...result, value];
  }, []);
}

function materializeNode(node: FlowchartNode): FlowchartNode {
  if (node.labelOrigin !== FlowchartLabelOrigin.Unresolved) {
    return node;
  }

  return {
    ...node,
    label: null,
    shape: FlowchartNodeShape.Process,
    parent: null,
  };
}

function resolveSubgraphEdges(parsed: ParsedFlowchart): ResolvedFlowchart {
  const subgraphIds = new Set(parsed.subgraphs.map((subgraph) => subgraph.id));
  const nodes = parsed.nodes.filter((node) => !subgraphIds.has(node.id));
  const edges = parsed.edges.map<FlowchartEdge>((edge) => ({
    ...edge,
    from: subgraphIds.has(edge.from) ? clusterAnchor(edge.from) : edge.from,
    to: subgraphIds.has(edge.to) ? clusterAnchor(edge.to) : edge.to,
  }));

  return { ...parsed, nodes, edges };
}

function materializeUnresolved(input: {
  nodes: readonly FlowchartNode[];
  edges: readonly FlowchartEdge[];
  subgraphIds: ReadonlySet<string>;
}): FlowchartNode[] {
  const materializedNodes = input.nodes.map((node) => materializeNode(node));
  const knownNodeIds = new Set(materializedNodes.map((node) => node.id));
  const referencedIds = unique(input.edges.flatMap((edge) => [edge.from, edge.to]));
  const missingPlaceholders = referencedIds
    .filter((id) => !knownNodeIds.has(id) && !input.subgraphIds.has(id))
    .map<FlowchartNode>((id) => ({
      id,
      label: null,
      labelOrigin: FlowchartLabelOrigin.Unresolved,
      shape: FlowchartNodeShape.Process,
      parent: null,
      classNames: [],
      inlineStyle: null,
    }));

  return [...materializedNodes, ...missingPlaceholders];
}

export function resolveFlowchart(parsed: ParsedFlowchart): ResolvedFlowchart {
  const withSubgraphEdges = resolveSubgraphEdges(parsed);
  const subgraphIds = new Set(withSubgraphEdges.subgraphs.map((subgraph) => subgraph.id));
  const nodes = materializeUnresolved({
    nodes: withSubgraphEdges.nodes,
    edges: withSubgraphEdges.edges,
    subgraphIds,
  });

  return { ...withSubgraphEdges, nodes };
}
